/**
 * The crosstab's own numbers, for DANI (M4S-5777 X11).
 *
 * Ask DANI inside a crosstab was told which questions are open, but not the numbers on screen, so
 * an answer quoted figures DANI worked out itself, with its own rules. This builds a compact,
 * size-capped summary of the table the engine computed (the last Run), which the workspace adds
 * to the page context it already registers, so DANI can quote the engine's figures: each table's
 * base, each row's percentage (or statistic) per column, and the significance letters.
 *
 * Telling DANI to quote these rather than query the data again is a prompt change on DANI's side
 * (docs/pdlc/crosstabs-one-engine.md §8); this only makes the numbers available.
 *
 * Pure.
 */

import type { ComputedTable } from '@/app/(private)/ai/components/Crosstabs/data';

/** Past this many characters the summary stops, and says so. */
export const DANI_TABLE_CONTEXT_MAX_CHARS = 12_000;

export interface DaniTableContext {
    /** The banner columns: letter, header, label and base. */
    columns: string;
    /** One line per row, `qid | row | col=value…`, in table order, cut at the cap. */
    rows: string;
    /** True when the cap cut the rows short. */
    truncated: boolean;
    /** The engine that computed these, for DANI's footnote. */
    source: string;
}

const pct = (n: number | null | undefined, decimals: number) =>
    n == null || !Number.isFinite(n) ? '–' : `${n.toFixed(decimals)}%`;

/**
 * The summary of a computed table. `decimals` is the crosstab's own (`decimalPlaces`), so a
 * figure DANI quotes reads as the table prints it.
 */
export function daniTableContext(
    computed: ComputedTable,
    { decimals = 0, maxChars = DANI_TABLE_CONTEXT_MAX_CHARS } = {},
): DaniTableContext {
    const groupOf = new Map<string, string>();
    for (const g of computed.banner) for (const c of g.cols) groupOf.set(c.id, g.group);
    const columns = computed.cols
        .map(
            (c) =>
                `${c.letter || '-'}: ${groupOf.get(c.id) ?? ''} / ${c.label} (n=${Math.round(computed.base[c.id] ?? 0)})`,
        )
        .join('\n');

    const lines: string[] = [];
    let used = columns.length;
    let truncated = false;
    outer: for (const block of computed.blocks) {
        const head = `# ${block.qid}: ${block.text}`;
        const blockLines = [head];
        for (const row of block.rows) {
            if (row.kind === 'base') {
                const bases = row.cells.map((c) => `${c.col}=${Math.round(c.base)}`).join(' ');
                blockLines.push(`${block.qid} | Base | ${bases}`);
                continue;
            }
            const cells = row.cells
                .map((c) => {
                    if (c.suppressed) return `${c.col}=suppressed`;
                    const value =
                        row.kind === 'stat'
                            ? c.statValue == null
                                ? '–'
                                : c.statValue.toFixed(Math.max(decimals, 1))
                            : pct(c.pct, decimals);
                    const marks = [...(c.letters ?? []), ...(c.letters2 ?? [])].join('');
                    return `${c.col}=${value}${marks ? ` ${marks}` : ''}`;
                })
                .join(' ');
            blockLines.push(`${block.qid} | ${row.label} | ${cells}`);
        }
        for (const line of blockLines) {
            if (used + line.length + 1 > maxChars) {
                truncated = true;
                break outer;
            }
            lines.push(line);
            used += line.length + 1;
        }
    }
    return {
        columns,
        rows: lines.join('\n'),
        truncated,
        source: 'AI Crosstabs engine (computeFromApi), last Run',
    };
}
